"use client"

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react"
import {
  Badge,
  Box,
  Flex,
  Text,
  Image,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  Portal,
  Tooltip,
} from "@chakra-ui/react"
import { ChevronDownIcon, CheckIcon, SearchIcon } from "@chakra-ui/icons"
import { useLocation } from "react-router-dom"
import { FaLayerGroup } from "react-icons/fa"
import { useSupplierRetailer } from "../wrappers/SupplierRetailerContext"
import { buildLogoCandidates, nextLogoCandidate, BLANK_LOGO } from "../tools/supplierLogo"
import { neutral, brand, type, radius, motion, elevation } from "../theme/tokens"
import { supplierLabel } from "../tools/supplierLabel"

/**
 * Supplier switcher.
 *
 * Hand-rolled rather than built on Chakra's Menu or Popover, after both fought
 * the two things this control needs:
 *
 *  - Menu owns a typeahead that swallows printable keys to jump between items,
 *    so a filter field inside it only ever received its first character.
 *  - Popover's `initialFocusRef` never landed on the field, and its popper
 *    wrapper is given `position:absolute; z-index:10` inline, which makes a
 *    stacking context that loses to the fixed rail at z-index 1000.
 *
 * The panel is portalled to <body>. An earlier version left it inside the rail
 * on `position: fixed`, which escaped the rail's `overflow: hidden` but not its
 * stacking context: the nav links are `position: relative` and come later in
 * the DOM, so with `z-index: auto` they painted — and hit-tested — above it.
 * Clicking a supplier activated the nav link behind it, and the wheel chained
 * into the rail's scroll box. Portalling plus an explicit z-index and
 * overscroll containment fixes both.
 */
const SupplierNavigation = ({ collapsed = false }) => {
  const { selectedSupplier, setSelectedSupplier, user_supplier_access, allSuppliersView, setAllSuppliersView } = useSupplierRetailer()
  // On the Rep page the switcher reads "Combined": that page spans every supplier of the
  // organisation, so the selection underneath is neither shown nor changeable there. It is
  // left untouched, and every other page returns to it (2026-09-21).
  const { pathname } = useLocation()
  const combined = pathname.startsWith("/rep")
  // On a rep's route page (/field/route) the list starts with "All suppliers" (2026-10-07).
  const allOffered = pathname.startsWith("/field/route")
  const showAll = allOffered && !!allSuppliersView

  const [open, setOpen] = useState(false)
  const [filter, setFilter] = useState("")
  const [anchor, setAnchor] = useState({ top: 0, left: 0 })

  const triggerRef = useRef(null)
  const panelRef = useRef(null)
  const searchRef = useRef(null)

  const candidatesByCode = useMemo(() => {
    const map = {}
    user_supplier_access.forEach(({ value }) => {
      map[value] = buildLogoCandidates(value)
    })
    return map
  }, [user_supplier_access])

  // Admins carry 30+ suppliers; scrolling that list was the slowest step in
  // switching context.
  const visible = useMemo(() => {
    const q = filter.trim().toLowerCase()
    if (!q) return user_supplier_access
    return user_supplier_access.filter(
      (s) => s.text?.toLowerCase().includes(q) || s.value?.toLowerCase().includes(q),
    )
  }, [user_supplier_access, filter])

  const showSearch = user_supplier_access.length > 8
  const logoFor = (code) => candidatesByCode[code]?.[0] || BLANK_LOGO

  const close = useCallback(() => {
    setOpen(false)
    setFilter("")
  }, [])

  useEffect(() => {
    if (combined) close()
  }, [combined, close])

  const choose = useCallback(
    (code) => {
      setSelectedSupplier(code)
      if (setAllSuppliersView) setAllSuppliersView(false)
      close()
    },
    [setSelectedSupplier, setAllSuppliersView, close],
  )

  // Anchor to the trigger before paint so the panel never flashes at 0,0.
  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return
    const r = triggerRef.current.getBoundingClientRect()
    setAnchor({ top: r.bottom + 6, left: Math.max(8, r.left) })
  }, [open])

  useEffect(() => {
    if (!open) return
    searchRef.current?.focus()

    const onPointerDown = (e) => {
      if (panelRef.current?.contains(e.target) || triggerRef.current?.contains(e.target)) return
      close()
    }
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        close()
        triggerRef.current?.focus()
      }
    }
    // Re-anchor rather than drift if the viewport changes while open.
    document.addEventListener("mousedown", onPointerDown)
    document.addEventListener("keydown", onKeyDown)
    window.addEventListener("resize", close)
    return () => {
      document.removeEventListener("mousedown", onPointerDown)
      document.removeEventListener("keydown", onKeyDown)
      window.removeEventListener("resize", close)
    }
  }, [open, close])

  const trigger = (
    <Flex
      as="button"
      type="button"
      ref={triggerRef}
      aria-haspopup="listbox"
      aria-expanded={open}
      aria-label="Change supplier"
      onClick={() => { if (!combined) setOpen((v) => !v) }}
      aria-disabled={combined}
      align="center"
      wrap="nowrap"
      gap={2}
      w="100%"
      h="38px"
      px={collapsed ? 0 : 2}
      justifyContent={collapsed ? "center" : "space-between"}
      cursor={combined ? "default" : "pointer"}
      borderRadius={radius.sm}
      border="1px solid rgba(255,255,255,0.16)"
      bg={open ? "rgba(255,255,255,0.16)" : "rgba(255,255,255,0.08)"}
      color={neutral[0]}
      transition={`background-color ${motion.fast}, border-color ${motion.fast}`}
      _hover={{ bg: "rgba(255,255,255,0.14)", borderColor: "rgba(255,255,255,0.28)" }}
      _focusVisible={{ outline: "2px solid rgba(255,255,255,0.7)", outlineOffset: "1px" }}
    >
      <Flex align="center" gap={2} minW={0}>
        {combined || showAll ? (
          <Flex align="center" justify="center" boxSize="22px" borderRadius={radius.sm} bg="rgba(255,255,255,0.18)" flexShrink={0}>
            <Icon as={FaLayerGroup} boxSize="12px" />
          </Flex>
        ) : (
          <Image
            key={selectedSupplier}
            src={logoFor(selectedSupplier)}
            onError={({ currentTarget }) =>
              nextLogoCandidate(currentTarget, candidatesByCode[selectedSupplier] || [])
            }
            alt=""
            boxSize="22px"
            borderRadius={radius.sm}
            objectFit="contain"
            bg={neutral[0]}
            flexShrink={0}
          />
        )}
        {!collapsed && (
          <Text
            fontSize={type.size.small}
            fontWeight={type.weight.semibold}
            whiteSpace="nowrap"
            overflow="hidden"
            textOverflow="ellipsis"
          >
            {combined ? "Combined" : showAll ? "All suppliers" : supplierLabel(selectedSupplier, user_supplier_access) || "Select supplier"}
          </Text>
        )}
      </Flex>
      {!collapsed && !combined && (
        <ChevronDownIcon
          boxSize="14px"
          opacity={0.7}
          flexShrink={0}
          transform={open ? "rotate(180deg)" : "none"}
          transition={`transform ${motion.fast}`}
        />
      )}
    </Flex>
  )

  return (
    <Box position="relative">
      {collapsed ? (
        <Tooltip label={combined ? "Combined — every supplier in the organisation" : supplierLabel(selectedSupplier, user_supplier_access) || "Select supplier"} placement="right" openDelay={200}>
          <Box>{trigger}</Box>
        </Tooltip>
      ) : (
        trigger
      )}

      {/* zIndex 1600 clears the rail (1000) and the sticky command bar (900). */}
      {open && !combined && (
        <Portal>
        <Box
          ref={panelRef}
          role="listbox"
          aria-label="Suppliers"
          position="fixed"
          zIndex={1600}
          top={`${anchor.top}px`}
          left={`${anchor.left}px`}
          w="260px"
          maxH="min(420px, calc(100vh - 120px))"
          display="flex"
          flexDirection="column"
          bg={neutral[0]}
          border="1px solid"
          borderColor={neutral[200]}
          borderRadius={radius.md}
          boxShadow={elevation.overlay}
          overflow="hidden"
        >
          {showSearch && (
            <Box p={2} borderBottom="1px solid" borderColor={neutral[200]}>
              <InputGroup size="sm">
                <InputLeftElement pointerEvents="none" h="32px">
                  <SearchIcon boxSize="12px" color={neutral[400]} />
                </InputLeftElement>
                <Input
                  ref={searchRef}
                  placeholder="Find supplier"
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  onKeyDown={(e) => {
                    // Enter takes the top match — the common case once you have
                    // typed three or four letters.
                    if (e.key === "Enter" && visible.length > 0) choose(visible[0].value)
                  }}
                />
              </InputGroup>
            </Box>
          )}

          {/* `contain` stops the wheel chaining out of the list once it hits
              top or bottom — without it, scrolling the suppliers scrolled the
              page and the nav rail behind. */}
          <Box overflowY="auto" overscrollBehavior="contain" p={1}>
            {visible.length === 0 && (
              <Text px={2} py={3} fontSize={type.size.small} color={neutral[500]}>
                No supplier matches “{filter}”.
              </Text>
            )}

            {allOffered && !filter.trim() && (
              <Flex
                as="button" type="button" role="option" aria-selected={showAll}
                onClick={() => { setAllSuppliersView(true); close() }}
                w="100%" align="center" gap={2} px={2} py={1.5} borderRadius={radius.sm} textAlign="left"
                bg={showAll ? brand[50] : "transparent"} color={showAll ? brand[600] : neutral[700]}
                fontSize={type.size.small} fontWeight={showAll ? type.weight.semibold : type.weight.regular}
                _hover={{ bg: showAll ? brand[50] : neutral[100] }}
              >
                <Flex align="center" justify="center" boxSize="22px" borderRadius={radius.sm} border="1px solid" borderColor={neutral[200]} flexShrink={0}>
                  <Icon as={FaLayerGroup} boxSize="12px" />
                </Flex>
                <Text>All suppliers</Text>
              </Flex>
            )}
            {visible.map((supplier) => {
              const isActive = !showAll && selectedSupplier === supplier.value
              return (
                <Flex
                  as="button"
                  type="button"
                  key={supplier.value}
                  role="option"
                  aria-selected={isActive}
                  onClick={() => choose(supplier.value)}
                  w="100%"
                  align="center"
                  gap={2}
                  px={2}
                  py={1.5}
                  borderRadius={radius.sm}
                  textAlign="left"
                  bg={isActive ? brand[50] : "transparent"}
                  color={isActive ? brand[600] : neutral[700]}
                  fontSize={type.size.small}
                  fontWeight={isActive ? type.weight.semibold : type.weight.regular}
                  _hover={{ bg: isActive ? brand[50] : neutral[100] }}
                  _focusVisible={{ outline: `2px solid ${brand[500]}`, outlineOffset: "-2px" }}
                >
                  <Image
                    src={logoFor(supplier.value)}
                    onError={({ currentTarget }) =>
                      nextLogoCandidate(currentTarget, candidatesByCode[supplier.value] || [])
                    }
                    alt=""
                    boxSize="22px"
                    borderRadius={radius.sm}
                    objectFit="contain"
                    border="1px solid"
                    borderColor={neutral[200]}
                    bg={neutral[0]}
                    flexShrink={0}
                  />
                  <Text flex="1" noOfLines={1}>
                    {supplier.text}
                  </Text>
                  {/* Account suspended — the report pages will not open for this
                      supplier (only the two master admins still see the data). */}
                  {supplier.unpaid && (
                    <Badge colorScheme="orange" variant="subtle" fontSize="9px" px={1} flexShrink={0}>
                      Suspended
                    </Badge>
                  )}
                  {isActive && <Icon as={CheckIcon} boxSize="11px" />}
                </Flex>
              )
            })}
          </Box>
        </Box>
        </Portal>
      )}
    </Box>
  )
}

export default SupplierNavigation
